import {Component, input, output} from '@angular/core';
import {AREA_COLOR_SWATCHES, EDITOR_ACCENT, FloorArea} from '../shared/models/table-layout.model';
import {withAlpha} from '../shared/models/floor-plan-geometry';

/**
 * The **body** of the floor-plan inspector while an area is selected (#201) — a literal port of the design
 * handover's `insp.isZone` branch: the colour swatch row and the note that explains the polygon gestures.
 *
 * The panel around it (colour chip, kind label, name field, close button, Duplizieren/Löschen) belongs to the host
 * screen, because the handover draws one inspector whose *body* swaps with the selection.
 *
 * An area's **outline is edited on the canvas**, not here — drag a vertex to reshape, click an edge to insert one,
 * double-tap a vertex to remove it, drag the fill to move the whole polygon (the handover's §5). That is what the
 * note spells out; there is deliberately no coordinate list, because the handover has none.
 *
 * Fully controlled: it emits a patched copy and never mutates its input.
 */
@Component({
  selector: 'app-area-editor',
  standalone: true,
  imports: [],
  styleUrl: './floor-plan-editor.scss',
  styles: [`
    :host {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .tp-swatches {
      display: flex;
      gap: 9px;
    }

    .tp-swatch {
      width: 34px;
      height: 34px;
      border-radius: 999px;
      box-shadow: inset 0 0 0 1px rgba(0, 0, 0, .1);
    }

    .tp-swatch.is-active {
      box-shadow: 0 0 0 2px #fff, 0 0 0 4px #c50e6c;
    }
  `],
  template: `
    @if (area(); as a) {
      <div>
        <div class="tp-section-title" id="area-color-label">Farbe</div>
        <div class="tp-swatches" role="group" aria-labelledby="area-color-label">
          @for (swatch of swatches; track swatch) {
            <button type="button" class="cq-btn tp-swatch" [class.is-active]="a.color === swatch"
                    [attr.aria-pressed]="a.color === swatch" [attr.aria-label]="'Farbe ' + swatch"
                    [style.background]="withAlpha(swatch, 0.9)"
                    (click)="recolor(swatch)"></button>
          }
        </div>
      </div>

      <p class="tp-note">
        Eckpunkte ziehen zum Verformen · auf eine Kante klicken fügt einen Knoten hinzu · Eckpunkt doppelt antippen
        entfernt ihn · Fläche ziehen zum Verschieben.
      </p>
    }
  `
})
export class AreaEditorComponent {
  /** The selected area, or `null` when nothing is selected. */
  readonly area = input<FloorArea | null>(null);

  /** Emitted with the full, patched area on every edit. */
  readonly areaChange = output<FloorArea>();

  protected readonly swatches = AREA_COLOR_SWATCHES;
  protected readonly accent = EDITOR_ACCENT;
  protected readonly withAlpha = withAlpha;

  /** Emits the area in a new colour. */
  recolor(color: string): void {
    const current = this.area();
    if (!current) return;
    this.areaChange.emit({...current, color});
  }
}
